import type { GridCamera } from "../../world-zoom.ts";

/**
 * The maths of sharp scaling for an in-between world zoom (P1.9-T5, ADR-061 decision 5): the world is drawn at
 * the next whole zoom into a buffer, where every source pixel is a whole block of texels on the buffer's grid,
 * and each device pixel then takes the box average of the buffer texels its span covers. A device pixel inside
 * one source pixel shows it exactly; a device pixel across the edge of two shows each in proportion to the
 * share it covers. Every source pixel is therefore exactly `zoom` device pixels wide counting blends by their
 * coverage, and with the step below 2 texels a device pixel meets at most two source pixels on an axis, so at
 * most one device pixel is blended between two.
 *
 * The world camera keeps the in-between zoom for everything but drawing (centring, the device-pixel snap, the
 * projection of world-attached UI and pointer input), and the plan derives the render camera from it each frame.
 * The render camera takes the world camera's own scroll, because Phaser multiplies a camera's scroll by each
 * object's scroll factor: any other scroll would move an object whose factor is not 1 away from where the world
 * camera puts it. Its pivot, its origin times its size, is what puts its render on whole texels.
 */

/**
 * The most buffer texels a device pixel's span meets on one axis, for any step below 2.
 */
export const MAX_TAPS = 3;

/**
 * How one frame renders the world at an in-between zoom. Written in place each frame, so planning allocates
 * nothing.
 */
export interface SharpZoomPlan {
  /** Whether the zoom is in between, so the world renders at `renderZoom` and is resampled. */
  active: boolean;
  /** The whole zoom the world renders at. */
  renderZoom: number;
  /** Buffer texels per device pixel: `renderZoom / zoom`. */
  step: number;
  /** The buffer's width in texels: every device pixel's span fits inside it. */
  bufferWidth: number;
  /** The buffer's height in texels. */
  bufferHeight: number;
  /** The render camera's scroll on x: the world camera's scroll as it renders. */
  scrollX: number;
  /** The render camera's scroll on y. */
  scrollY: number;
  /**
   * The render camera's pivot on x, in texels from the buffer's left edge (its origin times the buffer's width),
   * which puts the whole-zoom render's translation, `pivotX * (1 - renderZoom) - renderZoom * scrollX`, on a whole
   * texel.
   */
  pivotX: number;
  /** The render camera's pivot on y, in texels from the buffer's top edge. */
  pivotY: number;
  /** The buffer x, in texels from its left edge, where device x 0 falls; in [0, 1]. */
  originX: number;
  /** The buffer y, in texels from its top edge, where device y 0 falls; in [0, 1]. */
  originY: number;
}

/**
 * An inactive plan, to be written by `planSharpZoom`.
 *
 * @returns The plan.
 */
export function createSharpZoomPlan(): SharpZoomPlan {
  return { active: false, renderZoom: 1, step: 1, bufferWidth: 0, bufferHeight: 0, scrollX: 0, scrollY: 0, pivotX: 0, pivotY: 0, originX: 0, originY: 0 };
}

/**
 * The whole zoom an in-between zoom renders at.
 *
 * @param zoom The world camera's zoom.
 * @returns The next whole zoom above it, or null when the zoom is whole, below 1 or not finite, where the world
 * camera draws the world itself.
 */
export function renderZoomFor(zoom: number): number | null {
  if (!Number.isFinite(zoom) || zoom < 1 || Number.isInteger(zoom)) {
    return null;
  }
  return Math.ceil(zoom);
}

/**
 * Plans one axis: the render camera's pivot that, with the world camera's scroll, puts the whole-zoom render on
 * the buffer's texel grid over the world camera's view, and the buffer coordinate of the device grid's first edge.
 * The render's translation is the world camera's scaled by `renderZoom / zoom` and rounded up to a whole texel;
 * the pivot `p` solves `p * (1 - renderZoom) - renderZoom * scroll` equal to it.
 *
 * @param size The world camera's size on the axis, in device pixels.
 * @param origin The world camera's origin on the axis, as a fraction of the size.
 * @param zoom The world camera's zoom.
 * @param scroll The world camera's scroll on the axis as it renders, in world pixels.
 * @param renderZoom The whole zoom the world renders at, at least 2.
 * @param out Where to write: `pivot` and `origin`.
 */
function planAxis(size: number, origin: number, zoom: number, scroll: number, renderZoom: number, out: { pivot: number; origin: number }): void {
  const translation = size * origin * (1 - zoom) - zoom * scroll;
  const scaled = (translation * renderZoom) / zoom;
  const renderTranslation = Math.ceil(scaled);
  out.pivot = (renderTranslation + renderZoom * scroll) / (1 - renderZoom);
  out.origin = renderTranslation - scaled;
}

const axis = { pivot: 0, origin: 0 };

/**
 * Plans the frame for a world camera's view.
 *
 * @param view The world camera as it renders this frame: its size in device pixels, origin, zoom, and scroll
 * after its bounds clamp it.
 * @param out The plan to write.
 * @returns `out`, inactive when the zoom is whole.
 */
export function planSharpZoom(view: GridCamera, out: SharpZoomPlan): SharpZoomPlan {
  const renderZoom = renderZoomFor(view.zoom);
  if (renderZoom === null) {
    out.active = false;
    return out;
  }
  out.active = true;
  out.renderZoom = renderZoom;
  out.step = renderZoom / view.zoom;
  out.bufferWidth = Math.ceil((view.width * renderZoom) / view.zoom) + 1;
  out.bufferHeight = Math.ceil((view.height * renderZoom) / view.zoom) + 1;
  out.scrollX = view.scrollX;
  out.scrollY = view.scrollY;
  planAxis(view.width, view.originX, view.zoom, view.scrollX, renderZoom, axis);
  out.pivotX = axis.pivot;
  out.originX = axis.origin;
  planAxis(view.height, view.originY, view.zoom, view.scrollY, renderZoom, axis);
  out.pivotY = axis.pivot;
  out.originY = axis.origin;
  return out;
}

/**
 * The two linear-filtered reads that give one device pixel's box weights on an axis, in the closed form the
 * resample shader computes. A read at texel coordinate `t` (texel `i` centred at `i + 0.5`) under linear
 * filtering mixes the two texels around it by its distance from their centres. With the span starting at
 * `first + f` and `step` long (1 < step < 2), it meets two texels while `f + step <= 2`, weighted `1 - f` and
 * `f + step - 1`, which one read at `first + 0.5 + (f + step - 1) / step` reproduces; otherwise it meets three,
 * the middle one whole, and splitting the middle in proportion to the outer two puts the reads at the fixed
 * offsets `first + 0.5 + 1 / step` and `first + 2.5 - 1 / step`, the first weighing `(1 - f) / (step - 1)`. Both
 * cases are `min`s of the same terms, so the shader needs no branch and no division, and on two axes four
 * reads reproduce the nine box weights.
 *
 * @param start Where the span starts, in buffer texels.
 * @param step The span's length in texels, above 1 and below 2.
 * @param out Where to write: the first read's coordinate and weight, then the second's.
 */
export function linearTaps(start: number, step: number, out: Float64Array): void {
  const first = Math.floor(start);
  const f = start - first;
  const firstWeight = Math.min(1, (1 - f) / (step - 1));
  out[0] = first + 0.5 + Math.min(1, f + step - 1) / step;
  out[1] = firstWeight;
  out[2] = first + 2.5 - 1 / step;
  out[3] = 1 - firstWeight;
}

/**
 * The box weights of the buffer texels one device pixel's span covers on an axis: each texel's overlap with
 * the span, over the span's length.
 *
 * @param start Where the span starts, in buffer texels.
 * @param step The span's length in texels, below 2.
 * @param out Where to write the `MAX_TAPS` weights, for the texels from the returned one onwards.
 * @returns The first texel's index.
 */
export function boxWeights(start: number, step: number, out: Float64Array): number {
  const first = Math.floor(start);
  const end = start + step;
  for (let tap = 0; tap < MAX_TAPS; tap += 1) {
    const texel = first + tap;
    out[tap] = Math.max(0, Math.min(end, texel + 1) - Math.max(start, texel)) / step;
  }
  return first;
}
